import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { MsalProvider } from "@azure/msal-react";
import { loadAppConfig } from "./appConfig";
import { initAuth, initExternalIdAuth, getExternalIdInstance, msalInstance } from "./authConfig";
import App from "./App";
import { UserProvider } from "./context/UserContext";
import "./App.css";
import "./layout/Layout.css";

async function bootstrap() {
  // 1. Load runtime config from /config.json
  const config = await loadAppConfig();

  // 2. Initialise main Azure AD MSAL instance
  await initAuth(config);

  // 3. Initialise External ID MSAL instance (no-op if externalIdClientId is empty)
  await initExternalIdAuth(config);

  // 4. Process any pending External ID redirect BEFORE first render so that
  //    getAllAccounts() is reliable synchronously when components mount.
  const extId = getExternalIdInstance();
  if (extId) {
    try {
      const result = await extId.handleRedirectPromise();
      if (result?.account) {
        extId.setActiveAccount(result.account);
        console.info(`[ExtID] Redirect complete — tenantId: ${result.account.tenantId} | environment: ${result.account.environment} | homeAccountId: ${result.account.homeAccountId}`);
        // Store token so configApi can use it directly — MSAL v5 acquireTokenSilent
        // throws authority_mismatch for CIAM accounts even when authority is correct.
        sessionStorage.setItem("extid_token", JSON.stringify({
          accessToken: result.accessToken,
          expiresOn: result.expiresOn?.toISOString() ?? null,
        }));

        // If this redirect came from a genuine new sign-up (flag set by
        // handleTrialLogin before the redirect — see App.tsx), provision the
        // tenant/user now from the token's own claims (oid/email/name).
        // Company/job title are collected afterwards, in-app, by
        // CompleteTrialProfile — never re-call this for a returning user's
        // sign-in, since an empty body would overwrite their saved profile.
        const isNewSignup = sessionStorage.getItem("trial_signup_pending");
        if (isNewSignup) {
          try {
            // result.accessToken is already available from the redirect — no acquireTokenSilent needed
            const profileRes = await fetch(`${config.apiBase}/UpdateTrialProfile`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${result.accessToken}`,
              },
              body: "{}",
            });
            if (!profileRes.ok) {
              const errText = await profileRes.text();
              console.error("[ExtID] UpdateTrialProfile failed:", profileRes.status, errText);
            } else {
              console.info("[ExtID] Trial tenant provisioned.");
            }
          } catch (profileErr) {
            console.error("[ExtID] Failed to provision trial tenant:", profileErr);
          } finally {
            sessionStorage.removeItem("trial_signup_pending");
          }
        }
      }
    } catch (err) {
      console.error("[ExtID] Redirect processing failed:", err);
    }
  }

  // 5. Render the app
  const root = ReactDOM.createRoot(
    document.getElementById("root") as HTMLElement
  );

  root.render(
    <BrowserRouter>
      <MsalProvider instance={msalInstance as any}>
        <UserProvider>
          <App />
        </UserProvider>
      </MsalProvider>
    </BrowserRouter>
  );
}

bootstrap();
